'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { HEADER_HEIGHT } from '../constants/layout';

/**
 * The header's real height in pixels, measured rather than assumed.
 *
 * The layout used to offset content by the `HEADER_HEIGHT` constant, which
 * describes only the one-row desktop bar. The mobile header is two rows
 * wherever the search bar renders as a live input (`/search`, and any page that
 * registers a search scope), so a 64px offset left the top of the page buried
 * under the search strip. Measuring covers that case and any future row the
 * header grows, instead of adding a second constant per row.
 *
 * This is also what top-of-page content offsets itself by (the board's space
 * header, the invite banner): AppLayout only applies its own `marginTop` to
 * pages without a top banner, so those clear the fixed header themselves. They
 * cannot use a constant for the same reason this hook exists — the header grows
 * a row for the mobile search strip, and another for the runner update banner,
 * which can appear while the page is open.
 *
 * Both header elements carry `data-app-header`; only one is visible at a time
 * (the desktop bar is `md:flex`, the mobile one `md:hidden`), so the taller
 * measurement is the one on screen. A hidden element measures 0.
 */
export const useHeaderHeight = () => {
  const $ = _c(2);
  const [height, setHeight] = useState(HEADER_HEIGHT);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const nodes = Array.from(document.querySelectorAll("[data-app-header]"));
      if (nodes.length === 0) {
        return;
      }
      const measure = () => {
        const tallest = Math.max(...nodes.map(_temp));
        setHeight(tallest || HEADER_HEIGHT);
      };
      measure();
      const observer = new ResizeObserver(measure);
      for (const node_0 of nodes) {
        observer.observe(node_0);
      }
      return () => observer.disconnect();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return height;
};
function _temp(node) {
  return node.offsetHeight;
}